import React, { useCallback, useEffect, useState } from 'react';
import { auth } from '../firebase';
import { uploadModeratedMedia, mergeModerationTokens } from '../utils/moderatedUpload';
import type { ModerationTokenMap } from '../utils/moderatedUpload';
import { checkUploadSize } from '../utils/mediaApi';
import TopNavigation from '../components/TopNavigation';
import { useLocation, useNavigate } from 'react-router-dom';
import WalletTopupPayPal from '../components/WalletTopupPayPal';
import { aiScanAd } from '../utils/moderation';
import { LOCATIONS, GENERAL_INTERESTS } from '../utils/locations';
import { getWallet } from '../utils/wallet';
import { isUnlimitedAdsEligible } from '../utils/verification';
import { AdBillingFields, AdCtaFields, AdPlacementFields } from '../components/AdFormFields';
import { AdBillingModel, validateAdCta } from '../utils/adActions';
import { createAd, getPageById } from '../utils/secureFunctions';

const CreateAd: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [linkedPage, setLinkedPage] = useState<{ id: string; name?: string } | null>(null);
  
  // Basic ad details
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  // removed unused targetURL state
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  
  // Call to Action
  const [actionType, setActionType] = useState<'url' | 'call' | 'message' | 'whatsapp' | 'none'>('url');
  const [actionValue, setActionValue] = useState('');
  const [buttonText, setButtonText] = useState('Learn More');
  
  // Simplified targeting (user-facing only)
  const [targetInterests, setTargetInterests] = useState<string[]>([]);
  const [targetGender, setTargetGender] = useState<'male' | 'female' | 'all'>('all');
  const [targetAgeMin, setTargetAgeMin] = useState(18);
  const [targetAgeMax, setTargetAgeMax] = useState(65);
  
  // Location targeting with drill-down
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [selectedState, setSelectedState] = useState<string>('');
  const [selectedCity, setSelectedCity] = useState<string>('');
  
  // Placements
  const [placements, setPlacements] = useState<string[]>(['feed']);
  
  // Budget and auto-detected payment
  const [budget, setBudget] = useState(10);
  const [billingModel, setBillingModel] = useState<AdBillingModel>('cpm');
  const [topupAmount, setTopupAmount] = useState(10);
  const [showTopup, setShowTopup] = useState(false);
  const [topupKey, setTopupKey] = useState(0);
  const [payAmount, setPayAmount] = useState<number | null>(null);
  
  const [uploading, setUploading] = useState(false);
  const [walletBalanceUSD, setWalletBalanceUSD] = useState<number | null>(null);
  const [walletReservedUSD, setWalletReservedUSD] = useState<number | null>(null);
  const [walletLoading, setWalletLoading] = useState(true);
  const [walletError, setWalletError] = useState<string | null>(null);
  const [staffBypass, setStaffBypass] = useState(false);
  const normalizedBudgetUSD = budget;

  // Prefill from pageId if provided
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pageId = params.get('pageId');
    if (!pageId) return;
    const pageUrl = `${window.location.origin}/page/${pageId}`;
    // If a pageId is provided, default the CTA to open the page URL when not already set
    setActionType('url');
    setActionValue((prev) => prev || pageUrl);
    getPageById(pageId).then((res) => {
      const data = res.page as any;
      if (!data) return;
      setLinkedPage({ id: pageId, name: data.name });
      setTitle((t) => t || `Promote ${data.name || 'Page'}`);
      setDescription((d) => d || `Discover ${data.name} on Belivah`);
    }).catch(() => {});
  }, [location.search]);

  const refreshFundingState = useCallback(async () => {
    setWalletLoading(true);
    try {
      const user = auth.currentUser;
      if (!user) {
        setWalletBalanceUSD(null);
        setWalletReservedUSD(null);
        setStaffBypass(false);
        return null;
      }
      const wallet = await getWallet(user.uid);
      // User profile/role is resolved via VPS endpoints; default to no staff bypass if unknown.
      const staffOverride = isUnlimitedAdsEligible(null as any);
      const balanceUSD = wallet?.balanceUSD ?? 0;
      const reservedUSD = wallet?.reservedUSD ?? 0;
      setWalletBalanceUSD(balanceUSD);
      setWalletReservedUSD(reservedUSD);
      setStaffBypass(staffOverride);
      setWalletError(null);
      return { balanceUSD, reservedUSD, staffBypass: staffOverride };
    } catch (error) {
      console.error('[CreateAd] failed to fetch wallet info', error);
      setWalletError('Unable to load wallet balance. Please refresh.');
      return null;
    } finally {
      setWalletLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshFundingState();
  }, [refreshFundingState]);


  const walletBalanceDisplay = walletBalanceUSD ?? 0;
  const walletReservedDisplay = walletReservedUSD ?? 0;
  const walletCoversBudget = staffBypass || walletBalanceDisplay >= normalizedBudgetUSD;
  const walletDeficit = Math.max(0, normalizedBudgetUSD - walletBalanceDisplay);

  const handleInterestToggle = (interest: string) => {
    if (targetInterests.includes(interest)) {
      setTargetInterests(prev => prev.filter(i => i !== interest));
    } else {
      setTargetInterests(prev => [...prev, interest]);
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!auth.currentUser) {
      alert('Please log in');
      return;
    }

    if (!imageFile && !videoFile) {
      alert('Please upload at least an image or video for your ad');
      return;
    }

    if (placements.length === 0) {
      alert('Please choose at least one place to show your ad.');
      return;
    }

    const ctaProblem = validateAdCta(actionType, actionValue);
    if (ctaProblem) {
      alert(ctaProblem);
      return;
    }

    // Make location compulsory
    if (!selectedCountry) {
      alert('Please select at least a country for targeting');
      return;
    }

    const budgetUSD = normalizedBudgetUSD;
    const fundingSnapshot = await refreshFundingState();
    const staffOverride = fundingSnapshot?.staffBypass ?? staffBypass;
    const walletAvailableUSD = fundingSnapshot ? fundingSnapshot.balanceUSD : walletBalanceDisplay;
    if (!staffOverride && walletAvailableUSD < budgetUSD) {
      const deficit = Math.max(0, budgetUSD - walletAvailableUSD);
      alert("Insufficient wallet funds. Please add $" + deficit.toFixed(2) + " or lower the campaign budget.");
      return;
    }

    setUploading(true);

    try {
      let imageURL = '';
      let videoURL = '';

      let moderationTokens: ModerationTokenMap | undefined;

      if (imageFile) {
        const upload = await uploadModeratedMedia({
          folder: 'ads',
          field: 'imageURL',
          file: imageFile,
          contentType: imageFile.type || 'image/jpeg',
        });
        imageURL = upload.objectKey;
        moderationTokens = mergeModerationTokens(moderationTokens, upload);
      }

      if (videoFile) {
        const upload = await uploadModeratedMedia({
          folder: 'ads',
          field: 'videoURL',
          file: videoFile,
          contentType: videoFile.type || 'video/mp4',
        });
        videoURL = upload.objectKey;
        moderationTokens = mergeModerationTokens(moderationTokens, upload);
      }

      // AI scan
      const scan = await aiScanAd({ 
        title, 
        description, 
        targetURL: actionType === 'url' ? actionValue : undefined, 
        imageURL, 
        videoURL 
      });

      // Flatten location data for storage
      const locationTargets = {
        countries: selectedCountry ? [selectedCountry] : [],
        states: selectedCountry && selectedState ? { [selectedCountry]: [selectedState] } : {},
        cities: selectedCountry && selectedState && selectedCity ? { [selectedCountry]: { [selectedState]: [selectedCity] } } : {}
      };

      // Normalize budget to USD for internal storage


      // Create ad document - only include defined fields
      const adData: any = {
        title,
        description,
        actionType,
        actionValue: actionType === 'message' ? (linkedPage?.id || auth.currentUser.uid) : actionValue,
        targetGender,
        targetLocations: locationTargets,
        targetAgeMin,
        targetAgeMax,
        placements: placements as any[],
        budgetUsd: budgetUSD,
        billingModel,
        reviewStatus: scan.result === 'clean' ? 'auto_approve_on_payment' : 'needs_review',
        aiScan: { result: scan.result, reasons: scan.reasons, mode: 'client' as const },
      };

      // Only add optional fields if they have values
      if (imageURL) {
        adData.imageURL = imageURL;
      }
      if (videoURL) {
        adData.videoURL = videoURL;
      }
      if (actionType === 'url' && actionValue) {
        adData.targetURL = actionValue;
      }
      if (actionType !== 'none' && buttonText) {
        adData.buttonText = buttonText;
      }
      if (linkedPage?.id) {
        adData.pageId = linkedPage.id;
      }
      if (targetInterests.length > 0) {
        adData.targetInterests = targetInterests;
      }
      if (moderationTokens && Object.keys(moderationTokens).length > 0) {
        adData.moderationTokens = moderationTokens;
      }

      const created = await createAd(adData);
      // Ads are reviewed automatically by the API and funded from the advertiser wallet
      // (debited per impression/click). Only uncertain creatives are held for a moderator.
      alert(created?.status === 'active'
        ? 'Your ad is live! It will be shown to your audience while your wallet has funds.'
        : 'Ad created. Our safety check needs a quick manual look before it goes live.');
      navigate('/advertiser-dashboard');

    } catch (error) {
      console.error('Failed to create ad:', error);
      // Show detailed error message
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert(`Failed to create ad: ${errorMessage}\n\nPlease check the console for details.`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={styles.container}>
      <TopNavigation />
      <div style={styles.content}>
        <h1 style={styles.title}>Create Advertisement</h1>
        <p style={styles.subtitle}>Reach your target audience on Belivah</p>

        {linkedPage && (
          <div style={styles.pillInfo}>
            📄 Promoting: <strong>{linkedPage.name}</strong>
          </div>
        )}

        <form onSubmit={handleSubmit} style={styles.form}>
          {/* Basic Info */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Ad Details</h3>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Title *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                style={styles.input}
                placeholder="Your ad title"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Description *</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                style={styles.textarea}
                placeholder="Describe your ad"
                rows={3}
              />
            </div>

            <AdCtaFields
              actionType={actionType}
              actionValue={actionValue}
              buttonText={buttonText}
              onChange={(n) => { setActionType(n.actionType); setActionValue(n.actionValue); setButtonText(n.buttonText); }}
              styles={styles}
            />

            <div style={styles.formGroup}>
              <label style={styles.label}>Ad Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                style={styles.fileInput}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Ad Video (for video placements)</label>
              <input
                type="file"
                accept="video/*"
                onChange={(e) => { const f = e.target.files?.[0] || null; if (f && !checkUploadSize(f)) { e.target.value = ''; return; } setVideoFile(f); }}
                style={styles.fileInput}
              />
            </div>
          </div>

          {/* Audience Targeting */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Target Audience</h3>

            <div style={styles.formGroup}>
              <label style={styles.label}>Age Range: {targetAgeMin} - {targetAgeMax}</label>
              <div style={styles.ageInputs}>
                <div>
                  <label style={styles.smallLabel}>Min Age</label>
                  <input
                    type="number"
                    min="13"
                    max="100"
                    value={targetAgeMin}
                    onChange={(e) => setTargetAgeMin(Number(e.target.value))}
                    style={styles.ageInput}
                  />
                </div>
                <span style={{alignSelf: 'flex-end', padding: '8px'}}>to</span>
                <div>
                  <label style={styles.smallLabel}>Max Age</label>
                  <input
                    type="number"
                    min="13"
                    max="100"
                    value={targetAgeMax}
                    onChange={(e) => setTargetAgeMax(Number(e.target.value))}
                    style={styles.ageInput}
                  />
                </div>
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Gender</label>
              <select value={targetGender} onChange={(e) => setTargetGender(e.target.value as any)} style={styles.input}>
                <option value="all">All Genders</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Interests (Select what your audience cares about)</label>
              <div style={styles.checkboxGrid}>
                {GENERAL_INTERESTS.map(interest => (
                  <label key={interest} style={styles.checkbox}>
                    <input
                      type="checkbox"
                      checked={targetInterests.includes(interest)}
                      onChange={() => handleInterestToggle(interest)}
                    />
                    {interest}
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Location Targeting */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Location Targeting</h3>
            <p style={styles.hint}>Select country, then state and city for precise targeting</p>

            <div style={styles.formGroup}>
              <label style={styles.label}>Country</label>
              <select
                value={selectedCountry}
                onChange={e => {
                  setSelectedCountry(e.target.value);
                  setSelectedState('');
                  setSelectedCity('');
                }}
                style={styles.input}
              >
                <option value="">Select Country</option>
                {LOCATIONS.map(loc => (
                  <option key={loc.countryCode} value={loc.countryCode}>{loc.country}</option>
                ))}
              </select>
            </div>

            {selectedCountry && (
              <div style={styles.formGroup}>
                <label style={styles.label}>State/Province</label>
                <select
                  value={selectedState}
                  onChange={e => {
                    setSelectedState(e.target.value);
                    setSelectedCity('');
                  }}
                  style={styles.input}
                >
                  <option value="">Select State/Province</option>
                  {LOCATIONS.find(loc => loc.countryCode === selectedCountry)?.states?.map(state => (
                    <option key={state.name} value={state.name}>{state.name}</option>
                  ))}
                </select>
              </div>
            )}

            {selectedCountry && selectedState && (
              <div style={styles.formGroup}>
                <label style={styles.label}>City/Town</label>
                <select
                  value={selectedCity}
                  onChange={e => setSelectedCity(e.target.value)}
                  style={styles.input}
                >
                  <option value="">Select City/Town</option>
                  {LOCATIONS.find(loc => loc.countryCode === selectedCountry)?.states?.find(s => s.name === selectedState)?.cities.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Placements */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Where to Show Your Ad</h3>
            <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 8px' }}>Your ad is only shown in the places you tick, to people who match your audience.</p>
            <AdPlacementFields placements={placements} onChange={setPlacements} />
          </div>

          {/* Budget */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Budget</h3>
            <AdBillingFields value={billingModel} onChange={setBillingModel} budgetUsd={budget} />

            <div style={styles.infoBox}>
              {walletLoading ? (
                <span>Checking wallet balance…</span>
              ) : (
                <>
                  <strong>Wallet balance:</strong> ${walletBalanceDisplay.toFixed(2)} USD
                  {staffBypass ? (
                    <span> · Admin/mod privileges detected — no upfront funding required.</span>
                  ) : walletCoversBudget ? (
                    <span> · You're fully funded for this campaign.</span>
                  ) : (
                    <span> · Add ${walletDeficit.toFixed(2)} to cover this campaign.</span>
                  )}
                  {walletReservedDisplay > 0 && (
                    <div style={styles.walletReserved}>
                      Reserved funds: ${walletReservedDisplay.toFixed(2)} USD
                    </div>
                  )}
                </>
              )}
              {walletError && <p style={styles.errorText}>{walletError}</p>}
              <div style={styles.walletActions}>
                <button type="button" style={styles.walletButton} onClick={() => refreshFundingState()}>
                  Refresh balance
                </button>
                <button
                  type="button"
                  style={styles.walletButtonSecondary}
                  onClick={() => {
                    setTopupAmount(Math.max(1, Math.ceil(walletDeficit) || 10));
                    setPayAmount(null);
                    setShowTopup(true);
                  }}
                >
                  Fund wallet with PayPal
                </button>
              </div>
              {showTopup && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <label style={{ ...styles.label, marginBottom: 0 }} htmlFor="createad-topup-amount">Amount (USD $)</label>
                    <input
                      id="createad-topup-amount"
                      type="number"
                      min={1}
                      step={1}
                      value={topupAmount}
                      onChange={(e) => { setTopupAmount(Math.max(1, Math.floor(Number(e.target.value) || 1))); setPayAmount(null); }}
                      style={{ ...styles.input, maxWidth: 140 }}
                    />
                    <button type="button" style={styles.walletButton} onClick={() => { setPayAmount(topupAmount); setTopupKey((k) => k + 1); }}>
                      Continue to PayPal
                    </button>
                  </div>
                  {payAmount !== null && (
                    <WalletTopupPayPal
                      key={topupKey}
                      amount={payAmount}
                      onCredited={async () => { await refreshFundingState(); }}
                      onClose={() => { setShowTopup(false); setPayAmount(null); }}
                    />
                  )}
                </div>
              )}
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                Total Campaign Budget: ${budget.toLocaleString()} USD
              </label>
              <input
                type="range"
                min={10}
                max={10000}
                step={10}
                value={budget}
                onChange={(e) => setBudget(Number(e.target.value))}
                style={styles.range}
              />
              <div style={styles.formGroup}>
                <label style={styles.label}>Budget (USD $) *</label>
                <input
                  type="number"
                  min={10}
                  value={budget}
                  onChange={(e) => setBudget(Number(e.target.value))}
                  required
                  style={styles.input}
                />
              </div>
            </div>
          </div>
          {/* Submit */}
          <div style={styles.section}>
            <button type="submit" disabled={uploading} style={styles.submitBtn}>
              {uploading ? 'Processing…' : 'Create Advertisement'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#F5EDE1',
  },
  content: {
    maxWidth: 900,
    margin: '0 auto',
    padding: '20px',
  },
  title: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#072B2C',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    color: '#5B6E6E',
    marginBottom: 32,
  },
  pillInfo: {
    display: 'inline-block',
    background: '#E6F7F9',
    border: '1px solid #B7ECEF',
    color: '#0B6B6F',
    padding: '6px 12px',
    borderRadius: 999,
    marginBottom: 16,
    fontSize: 14,
  },
  form: {
    background: '#fff',
    borderRadius: 12,
    padding: 32,
  },
  section: {
    marginBottom: 40,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#072B2C',
    marginBottom: 16,
    borderBottom: '2px solid #66EAD1',
    paddingBottom: 8,
  },
  formGroup: {
    marginBottom: 20,
  },
  label: {
    display: 'block',
    fontSize: 14,
    fontWeight: '500',
    color: '#072B2C',
    marginBottom: 8,
  },
  smallLabel: {
    display: 'block',
    fontSize: 12,
    color: '#666',
    marginBottom: 4,
  },
  input: {
    width: '100%',
    padding: 12,
    borderRadius: 8,
    border: '1px solid #ccc',
    fontSize: 14,
  },
  ageInput: {
    width: 100,
    padding: 10,
    borderRadius: 8,
    border: '1px solid #ccc',
    fontSize: 14,
  },
  ageInputs: {
    display: 'flex',
    gap: 12,
    alignItems: 'flex-end',
  },
  textarea: {
    width: '100%',
    padding: 12,
    borderRadius: 8,
    border: '1px solid #ccc',
    fontSize: 14,
    resize: 'vertical',
  },
  fileInput: {
    width: '100%',
    padding: 8,
    fontSize: 14,
  },
  hint: {
    fontSize: 12,
    color: '#999',
    marginTop: 4,
  },
  checkboxGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))',
    gap: 12,
  },
  locationBlock: {
    marginBottom: 20,
    padding: 16,
    background: '#f9f9f9',
    borderRadius: 8,
  },
  countryLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 16,
    cursor: 'pointer',
    marginBottom: 12,
  },
  statesContainer: {
    paddingLeft: 24,
  },
  stateBlock: {
    marginBottom: 12,
  },
  stateLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 14,
    fontWeight: '500',
    cursor: 'pointer',
    marginBottom: 8,
  },
  citiesContainer: {
    paddingLeft: 24,
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
    gap: 8,
  },
  cityLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    cursor: 'pointer',
  },
  range: {
    width: '100%',
    marginTop: 8,
  },
  infoBox: {
    background: '#E8F5F1',
    padding: 20,
    borderRadius: 8,
    fontSize: 14,
    marginTop: 16,
    textAlign: 'center',
  },
  walletReserved: {
    marginTop: 8,
    fontSize: 13,
    color: '#0B6B6F',
  },
  walletActions: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
    marginTop: 12,
  },
  walletButton: {
    padding: '8px 14px',
    borderRadius: 999,
    border: '1px solid #0B6B6F',
    background: '#0B6B6F',
    color: '#fff',
    fontSize: 13,
    cursor: 'pointer',
  },
  walletButtonSecondary: {
    padding: '8px 14px',
    borderRadius: 999,
    border: '1px solid #0B6B6F',
    background: 'transparent',
    color: '#0B6B6F',
    fontSize: 13,
    cursor: 'pointer',
  },
  errorText: {
    color: '#B42318',
    marginTop: 8,
    fontSize: 13,
  },
  submitBtn: {
    width: '100%',
    padding: 16,
    background: '#0B6B6F',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 16,
    fontWeight: '600',
    cursor: 'pointer',
  },
};

export default CreateAd;
